/**
 * CJAS — roles and permissions.  Jira CA-17, answering CA-90.
 *
 * The governing rule for the public side is unchanged: EVERYTHING is open
 * without login except donations, giving history and member content. A visitor
 * who never signs in must get a fully useful app — prayer times, events,
 * services, Qibla, library.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE WAS REWRITTEN (2026-09-09)
 *
 * Until today four files disagreed about what a role even was. `access.js`
 * had a four-rung ladder (visitor·member·staff·admin); the database enum
 * `staff_role` had three different rungs (editor·approver·admin);
 * `composer.js` and `inbox.js` both checked for `admin` or **`imam`**, a role
 * that existed in no schema anywhere. The practical consequence: **a user
 * stored as `approver` could not approve anything**, because the only code
 * that granted approval accepted `admin` or `imam`.
 *
 * CJAS answered it on CA-90: *"We need to create role and permission based
 * system. Like super admin can give access to members what kind of actions
 * they can do. like admin, editor and other levels."*
 *
 * So the primitive is the PERMISSION, not the rank. Roles are named bundles
 * of permissions — a convenience and a sensible default, not a ladder — and a
 * super admin can grant or revoke individual permissions on top of a role.
 * There is no `RANK` any more: rank is what made "staff or above" a check
 * somebody could write, and it is exactly what stopped `approver` working.
 *
 * `imam` was NOT a role then. It never was one anywhere it could be stored. What
 * it actually meant — "may read the confidential detail on a fatiha or nikah
 * request" — is now the permission `requests.read_confidential`, which a super
 * admin grants to whoever the mosque decides. A flag, not a rung. (2026-10-25:
 * the owner made `imam` a real role — the imam's day-to-day bundle, see
 * ROLE_DEFS — and it does NOT carry requests.read_confidential; that stays a
 * grant.)
 * ────────────────────────────────────────────────────────────────────────
 *
 * Checks live here so they can be enforced on the server. A permission
 * implemented only in the UI is not a permission.
 */

/**
 * Every capability in the platform. A permission not in this list is DENIED,
 * always — an unknown string is a typo or an attack, never a grant.
 */
const PERMISSIONS = [
  // --- open to everyone, signed in or not ---
  'prayer.read', 'events.read', 'notices.read', 'services.submit',
  // 'partners.read' is medlemsfordeler — WHICH shops give members a discount
  // (CA-128). Open to everyone on purpose: which shops partner with the
  // mosque is not personal data, and somebody deciding whether to join is
  // exactly who should be able to read it. Claiming the discount still needs
  // a card, and a card still needs a membership.
  'library.read', 'screens.display', 'partners.read',

  // --- requires a signed-in member ---
  'member.card.read', 'member.giving.read', 'donation.create', 'store.buy', 'member.sharing.set',
  // Asking a mosque for membership, withdrawing the request, and asking to leave (owner, 2026-10-11). Every signed-in
  // app account holds it: signing in gives an account, and this is how an account ASKS — the mosque decides.
  'membership.request',

  // --- day-to-day staff work ---
  // Recording a withdrawal that arrived by letter, e-mail, phone or at the desk (owner, Phase 1, 2026-10-11). Recording
  // only: there is no permission to refuse or delay a withdrawal, because no such action exists.
  'membership.withdrawal.record',
  'prayer.write', 'events.write', 'notices.write', 'library.write',
  'translations.review', 'requests.read', 'requests.write',
  'screens.manage', 'members.link', 'push.compose',
  // Security fix AZ-6 (2026-10-29): the FULL list of withdrawals received (names, member numbers, states). Recording one
  // (membership.withdrawal.record) stays the editor's; reading them all is the approver tier's.
  'membership.withdrawal.list',
  // Batch 1 hardening (2026-10-02): the panel's general operational picture — the dashboard (member, request, screen
  // and app-user counts), the admin notice list with its drafts, the push audience, and the prayer-time administration
  // reads (times, timetable runway, Hijri adjustment). Those routes used to ask only for a staff account or for a
  // PUBLIC permission (notices.read, prayer.read), which every account inherits, so an editor with everything revoked
  // still read them. Editor tier, so every existing staff role keeps exactly what it had; a volunteer account has it
  // revoked (VOLUNTEER_KEEPS below).
  'operations.read',

  // --- the second pair of eyes ---
  'notices.approve', 'push.approve',
  // Banner hardening (2026-10-03): putting a banner on the walls (and, when chosen, in the member app) — publishing it
  // now or scheduling it — is somebody else's decision than making it. Never the person who made or last changed it
  // (core banners.transition, cannot_approve_own).
  'banners.approve',
  // Deciding membership requests: approve (choosing the member in the register), reject, complete a removal the
  // member asked for (owner, 2026-10-11). A decision about who belongs to the mosque, so not every editor's.
  'membership.decide',
  // The independent register (owner, Phase 1, 2026-10-11). Creating a PERSON is not membership; recording a joining
  // declaration and confirming it are separate permissions, and the confirmer can never be the recorder.
  'members.register', 'membership.declaration.record', 'membership.declaration.confirm',

  // --- administration ---
  // Ending a membership for a reason that is not a withdrawal (duplicate, deceased, correction), and correcting a
  // withdrawal's received date with a reason code.
  'membership.end',
  // Lifting the 30-day wait after a rejection so the person may ASK again (owner, 2026-10-11). Never a decision.
  'membership.reapply.release',
  'members.import', 'audit.read', 'settings.write',
  // What "imam" used to mean. Confidential detail on a fatiha or a nikah
  // request — a family's circumstances, not an operational field.
  'requests.read_confidential',
  // Entering a signed paper form as a member's answer (membership consent).
  // Its own permission, not part of members.import: it CREATES evidence the
  // mosque will show the state, so who may do it is decided on purpose.
  'consent.record_paper',
  // OA-002: see the mosque's Vipps gifts and their payment state. The giver is never named in the list; financial
  // data is admin-tier.
  'donations.read',
  // QA A14 (2026-10-16): «Trekk nå» / «Sjekk nå» asks Vipps again and CAPTURES an authorised gift — it moves money, so
  // it is not reading. It was allowed with donations.read alone, which a treasurer's helper may be granted to look.
  // Admin tier, like donations.read: every admin keeps the button; anyone else only by a named grant under Tilganger.
  'donations.capture',
  // OA-012: see the mosque's retention categories, policies and run counts (never the data itself).
  'retention.read',
  // The mosque's own privacy page (2026-10-14): see it, publish a new version, put an earlier one back. Admin tier: the
  // page is the mosque's statement to everyone whose data it keeps, and a new one pauses whatever Min Masjid approved for
  // the old one until it approves the new one. Approving it is Min Masjid's (operators' console), never a permission here.
  'privacy.manage',
  // Fødselsnummer in the register (owner decision 2026-10-15; behind the `national_id` capability, which is not entitled
  // for any mosque and is gated by its own privacy scope). See it unmasked («Vis», audited as view_member every time) and
  // record or correct it from a paper form. Admin tier: the number together with membership reveals religion (GDPR
  // art. 9). Nobody below admin sees even the masked form; the person page asks for this permission and the capability.
  'members.national_id',
  // OA-028: WHO answered an event's RSVP (counts are for anyone with events.write). Attendance at a religious
  // event is sensitive: admin tier, and every look is audited.
  'events.attendees.read',
  // Store foundation (staging only): the mosque's products and draft orders — read and write. Its own permission,
  // not donations.read: changing what is sold and for how much is not reading a gift list. Admin tier. The routes
  // also need the `store` capability, which is off and not entitled for any mosque.
  'store.manage',
  // MKK adoption Batch 2A (event operations): a staff-kept registration list for an event — set it up, add people,
  // change, cancel, move from the waiting list — and reading its names. Admin tier, like events.attendees.read: who
  // comes to a religious event is sensitive. Behind the event_registration capability (not entitled for any mosque).
  'events.registrations.manage',
  // Batch 2A: check people in at the door, and undo. Reads the names it needs to. Its own permission, so a super admin
  // can give the door to someone who does not manage the list. Behind event_checkin.
  'events.checkin',
  // Prayer-time connector framework (behind the `timetable_source` capability, not entitled for any mosque). Narrow on
  // purpose — reading the source card, uploading for preview, approving (and so publishing) or rejecting, rolling back,
  // and choosing the source are five different decisions, and a mosque can give them to five different people.
  'timetable.source.read',      // the source card, versions, previews
  'timetable.source.upload',    // upload a file for preview; nothing published
  'timetable.source.approve',   // approve (publish) or reject a pending version
  'timetable.source.rollback',  // put back the version before the published one
  'timetable.source.settings',  // which source the mosque uses
  // MKK adoption Batch 1: internal staff work (behind the `tasks`, `checklists` and `planner` capabilities). Editor tier —
  // running the week is day-to-day staff work — and shaped per person with revokes: a volunteer's account is an editor
  // with everything revoked but work.assigned (MKK_ADOPTION_BATCH_1.md §2). Seeing your own work and running everyone's
  // are different permissions.
  'work.assigned',       // my tasks (status, comments) and my checklist items; unassigned items in open runs
  'tasks.manage',        // every task: create, assign, edit, cancel
  'checklists.manage',   // templates; start, cancel and assign runs; tick any item
  'planner.read', 'planner.write',
  // The khutbah plan (MKK adoption A1, 2026-10-24; behind the `khutbah_plan` capability): who speaks, in which language,
  // on what, every Friday — read, write and delete, and the «Vis i appen» switch. Admin tier, and the `imam` role's
  // (2026-10-25); granted by name to anyone else who keeps the plan.
  'khutbah.manage',
  // Screen remote from the phone (A2, 2026-10-24): put a banner, a notice or a silence screen on a screen or a group
  // for a set time, and end it. Its own permission, so a super admin can hand the hall's screens to whoever stands in
  // the hall without giving them the screen settings (screens.manage). Admin tier by default; grantable by name.
  'screens.remote',
  // Programme runner (R3, MKK module 30, 2026-10-29; behind the `programme_runner` capability): start an event's programme
  // on chosen screens, move through it (next, previous, jump), pause it and end it — from the phone or the panel — and
  // keep the programme's steps. The person running the evening: editor tier (so approver and admin inherit it), the
  // imam and the secretary. Writing the event itself stays events.write; the steps may be kept by either.
  'programme.run',
  // MKK module 13 «Kontakter» (B1, 2026-10-24; behind the `contacts_directory` capability): the mosque's directory of the
  // people it works with. Reading (and calling from the phone) is editor tier; adding, changing, archiving is admin tier.
  // A volunteer has neither: the directory is third parties' numbers, not the volunteer's own work.
  'contacts.read',       // see and search the directory
  'contacts.manage',     // add, change, archive and restore a contact
  // MKK module 23 «Utlegg» (B2, 2026-10-24; behind the `expenses` capability): a staff member's claim for money they laid
  // out. Every staff role submits its own, the volunteer included (they buy the iftar food); deciding and paying are
  // admin tier. Four eyes is a rule on top (core expenses.decision): nobody decides their own claim, whatever they hold.
  'expenses.submit',     // my own claims: write, change while submitted, withdraw, add receipts
  'expenses.approve',    // every claim: approve or reject (never one's own)
  'expenses.pay',        // every claim: mark an approved claim paid; the CSV for the accountant
  // Budsjett (money-pro G1, 2026-10-31; behind the `budget` capability): the year's budget lines, budget against actual,
  // the CSV for the accountant. Admin and the treasurer.
  'finance.budget',      // write the budget and read it beside the actual income and costs
  // MKK module 21 «Innsamling» (owner 2026-10-29; behind the `campaigns` capability): fundraising campaigns. Reading them
  // with their totals and the monthly report is the board's (every board role) and admin's; writing, activating and
  // closing them is the treasurer's and admin's. No donor is ever in either: totals and counts only.
  'campaigns.read',      // every campaign with its progress; the monthly report (CSV)
  'campaigns.manage',    // create, edit, activate and close a campaign
  // MKK module 22 «Kontantgaver» (R2, 2026-10-29; behind the `cash_gifts` capability): cash handed to the mosque. One
  // person records, ANOTHER confirms (four eyes is a rule on top, core cash-gifts.confirmation and a database CHECK:
  // nobody confirms what they recorded, whatever they hold); confirming numbers the receipt. Admin and the treasurer hold
  // all three; the chair confirms.
  'cash_gifts.record',   // record a cash gift, reject one's own before confirmation, record a correction; see, export, receipts
  'cash_gifts.confirm',  // confirm or reject somebody else's entry (never one's own); see, export, receipts
  'cash_gifts.lock',     // lock a month: nothing dated in it is recorded or confirmed afterwards
  // MKK module 15 «Intern info» + «Viktige dokumenter» (C1, 2026-10-25; behind the `documents` capability): the mosque's
  // own archive. Reading and keeping it is editor tier (staff and the board run the paperwork); a link that opens a file
  // for someone OUTSIDE the mosque is admin tier. A volunteer has none of them.
  'documents.read',      // see, search and download the archive
  'documents.manage',    // folders; add, change, replace, archive and restore documents
  'documents.share',     // make a sharing link that expires, and revoke it
  // MKK module 18 «Styremøte» (C2, 2026-10-25; behind the `board_meetings` capability). Reading is the board's
  // (board_member and the roles on it, admin and super admin); running the meetings is the secretary's, the chair's and
  // admin tier (owner, 2026-10-25: real mosque roles — a plain editor no longer reads them). A volunteer has neither.
  'board.read',          // see the meetings, the agenda, the decisions; set one's OWN attendance; download the minutes
  'board.manage',        // create and change meetings and items, everybody's attendance, call, hold, cancel, minutes
  // MKK module 19 «Avstemninger» (C3, 2026-10-25; behind the `board_voting` capability): proposals and votes in the board.
  // Voting is the board's (board_member, secretary, treasurer, board_chair, and admin and super admin); running a vote is
  // the chair's and admin tier (owner, 2026-10-25: a plain editor or approver no longer votes). Who actually votes in a
  // given vote is its eligible list, picked from those who hold voting.vote. A volunteer has neither.
  'voting.vote',         // see the votes one is eligible in, cast one's ballot, see the results after closing
  'voting.manage',       // write drafts, open, close and cancel votes; see every vote, the turnout and the live count
  // WhatsApp, phase 1 (MKK module 37, 2026-10-24; behind the `whatsapp` capability): a message made from a notice, an
  // event, a banner or free text and shared from the staff member's own phone — nothing is sent by the platform. Sharing
  // is what announcement writers do (editor tier); which groups and channels the mosque keeps is the admin's.
  'whatsapp.send',       // compose, preview, share from the phone, and see the log of what was shared
  'whatsapp.manage',     // add, change, archive and restore the mosque's groups, channels and contacts

  // --- the super admin's own powers ---
  'staff.manage',        // create, deactivate and re-role staff users
  'permissions.grant',   // change what an individual staff user may do
  // OA-013: switch the mosque's own manageable capabilities (what the mosque HAS).
  // A capability is not a permission: this permission only operates the switch.
  'capabilities.manage',
  // OA-012: decide how long the mosque keeps a category of personal data (period, dry run, enforce). Deletes data
  // once enforced, so it is the super admin's, beside who may sign in at all.
  'retention.manage',
  // Batch 7.0 (owner, 2026-10-04): send a routine push INSIDE quiet hours as an emergency override (push_quiet_hours,
  // activation-blocked). Required IN ADDITION to push.approve, which the send route asks for first — never instead of
  // it. Default holders (placeholder, to be confirmed — push-delivery PLACEHOLDERS): the super admin only, like the
  // super admin's own powers above; anyone else only by a named grant under «Tilganger».
  'push.emergency',
  // Declaring which app account belongs to a staff member, so their own application is decided by someone else.
  'staff.app_account.declare',

  // --- partner shops (CA-128) ---
  'partners.manage',     // register a shop, switch it off, read its scan log
  // The ONE thing a partner shop can do: ask "is this card valid?" and be
  // told yes/no plus a first name. Partner-only — see PARTNER_ONLY below.
  'card.verify',
];

/**
 * Capabilities that belong to a THIRD PARTY and to nobody inside the mosque.
 *
 * `card.verify` answers a question about a named person's membership of a
 * mosque — data about religion (GDPR art. 9) — to a shop. It is granted to a
 * registered partner and to nobody else: not to a super admin (who has the
 * member register already and has no reason to go through the shop's door),
 * and never by hand to a staff user. Everything that makes a partner scan
 * acceptable — the shop's own key, the per-shop log, the switch-off — exists
 * only on the partner path. CA-128.
 */
const PARTNER_ONLY = ['card.verify'];

/**
 * Roles as bundles. `staff: false` marks the two public tiers — they describe
 * an app user, and a staff permission can never be granted to one, because
 * "member" is not an account somebody signs into the admin panel with.
 */
const ROLE_DEFS = {
  visitor: { staff: false, permissions: [
    'prayer.read', 'events.read', 'notices.read', 'services.submit',
    'library.read', 'screens.display', 'partners.read',
  ] },
  member: { staff: false, inherits: 'visitor', permissions: [
    'member.card.read', 'member.giving.read', 'donation.create', 'store.buy', 'member.sharing.set', 'membership.request',
  ] },
  editor: { staff: true, inherits: 'member', permissions: [
    'prayer.write', 'events.write', 'notices.write', 'library.write',
    'translations.review', 'requests.read', 'requests.write',
    // Security fix AZ-4/AZ-6 (2026-10-29): members.link — linking any app account to any member, guardians, the whole
    // register paged — moved to the approver tier (below). An editor keeps recording a received withdrawal.
    'screens.manage', 'push.compose', 'operations.read', 'membership.withdrawal.record',
    'timetable.source.read', 'timetable.source.upload',
    'work.assigned', 'tasks.manage', 'checklists.manage', 'planner.read', 'planner.write',
    'contacts.read',
    'expenses.submit',
    'documents.read', 'documents.manage',
    'whatsapp.send',
    'programme.run',
  ] },
  approver: { staff: true, inherits: 'editor', permissions: [
    'notices.approve', 'push.approve', 'timetable.source.approve', 'banners.approve', 'membership.decide',
    'members.register', 'membership.declaration.record', 'membership.declaration.confirm',
    // AZ-4/AZ-6 (2026-10-29): from the editor tier — one person linking an app account to a member, and reading the
    // register and every withdrawal, is the approver's.
    'members.link', 'membership.withdrawal.list',
  ] },
  admin: { staff: true, inherits: 'approver', permissions: [
    'members.import', 'audit.read', 'settings.write', 'requests.read_confidential',
    'partners.manage', 'consent.record_paper', 'donations.read', 'donations.capture', 'retention.read', 'privacy.manage', 'members.national_id', 'events.attendees.read', 'store.manage',
    'membership.end', 'membership.reapply.release', 'timetable.source.rollback', 'timetable.source.settings',
    'events.registrations.manage', 'events.checkin', 'khutbah.manage', 'screens.remote', 'contacts.manage', 'expenses.approve', 'expenses.pay', 'documents.share', 'board.manage', 'voting.manage', 'whatsapp.manage',
    'campaigns.read', 'campaigns.manage',
    'cash_gifts.record', 'cash_gifts.confirm', 'cash_gifts.lock',
    'finance.budget',
    // Real mosque roles (owner, 2026-10-25): board.read and voting.vote left the editor — the board is not the office —
    // and are named here so that admin and super admin keep them.
    'board.read', 'voting.vote',
  ] },
  super_admin: { staff: true, inherits: 'admin', permissions: [
    'staff.manage', 'permissions.grant', 'capabilities.manage', 'retention.manage', 'push.emergency', 'staff.app_account.declare',
  ] },
  // Batch 1 remediation (2026-10-02): a VOLUNTEER signs in to the panel and sees their own work — the tasks assigned to
  // them, and their own checklist items, ticked and unticked — and nothing else. It inherits NOTHING (not editor, not
  // member, not visitor), so a permission added to any other role later can never reach it, and grants are ignored
  // (permissionsFor) and refused (changePermissions): to give a volunteer more, change their role.
  // MKK module 23 «Utlegg» (2026-10-24): a volunteer may also claim back what they laid out (expenses.submit) — their own
  // claims only, never anyone else's.
  volunteer: { staff: true, volunteer: true, permissions: ['work.assigned', 'expenses.submit'] },
  /*
   * Real mosque roles (owner, 2026-10-25). The positions a mosque actually has, each a bundle on top of `member` — NOT
   * on top of editor, so a board member does not run the office's work by being on the board, and the imam does not
   * hold the requests inbox or the screen settings by leading the prayer. Grants and revokes work on them as on any role.
   * Where a route needs a second permission to be usable the bundle carries it too (the imam's operations.read: the
   * notice list, the prayer-time page and the push audience are behind it); nothing else is added.
   */
  // The imam: the khutbah plan, notices, the programme, the prayer times, a push, the hall's screens from the phone, and
  // a WhatsApp message.
  imam: { staff: true, inherits: 'member', permissions: [
    'khutbah.manage', 'notices.write', 'events.write', 'prayer.write', 'push.compose', 'screens.remote',
    'contacts.read', 'documents.read', 'expenses.submit', 'work.assigned', 'whatsapp.send', 'operations.read',
    'programme.run',
  ] },
  // A board member: reads the board's meetings and votes in its votes; reads the archive, the contacts and the year plan.
  board_member: { staff: true, inherits: 'member', permissions: [
    'board.read', 'voting.vote', 'documents.read', 'contacts.read', 'expenses.submit', 'work.assigned', 'planner.read', 'operations.read',
    // Innsamling (2026-10-29): the board follows the campaigns and their totals.
    'campaigns.read',
  ] },
  // The secretary: a board member who keeps the meetings, the minutes, the archive and the contacts, and writes notices.
  secretary: { staff: true, inherits: 'board_member', permissions: [
    'board.manage', 'documents.manage', 'documents.share', 'contacts.manage', 'whatsapp.send', 'notices.write',
    'programme.run',
  ] },
  // The treasurer: a board member who approves and pays expense claims, sees the gifts and runs the campaigns; records,
  // confirms (never their own) and locks cash gifts (2026-10-29).
  treasurer: { staff: true, inherits: 'board_member', permissions: [
    'expenses.approve', 'expenses.pay', 'donations.read', 'campaigns.manage', 'cash_gifts.record', 'cash_gifts.confirm', 'cash_gifts.lock',
    // Budsjett (2026-10-31).
    'finance.budget',
  ] },
  // The chair: a board member who runs the meetings and the votes, keeps the archive and the contacts, and approves claims.
  board_chair: { staff: true, inherits: 'board_member', permissions: [
    'board.manage', 'voting.manage', 'documents.manage', 'documents.share', 'expenses.approve', 'contacts.manage',
    // Kontantgaver (2026-10-29): the chair is the second pair of eyes on a cash gift.
    'cash_gifts.confirm',
  ] },
  // A registered partner shop (CA-128). Not staff, not an app user, inherits
  // nothing: it cannot read a prayer time through this account, let alone a
  // member. It signs in with its own key on the partner path only.
  partner: { staff: false, partner: true, permissions: ['card.verify'] },
};

const ROLES = Object.keys(ROLE_DEFS);
const STAFF_ROLES = ROLES.filter(r => ROLE_DEFS[r].staff);

/**
 * The OLD way to make a volunteer (Batch 1 hardening, before the `volunteer` role): an EDITOR with every editor-tier
 * permission revoked but these. Stored revokes are fixed rows, so every editor permission added later reached such an
 * account (operations.read did). Kept only to FIND those accounts and move them to the role (volunteerMigration);
 * never to make a new one.
 */
const VOLUNTEER_KEEPS = ['work.assigned'];
function volunteerRevokes({ withPlanner = false } = {}) {
  const keep = new Set([...VOLUNTEER_KEEPS, ...(withPlanner ? ['planner.read'] : [])]);
  const member = new Set(permissionsForRole('member'));
  return permissionsForRole('editor').filter(p => !member.has(p) && !keep.has(p));
}

/**
 * Permissions an old-style volunteer kept, or gained afterwards without anyone deciding it: `planner.read` (the
 * optional one) and the editor-tier permissions added after Batch 1 (`operations.read`).
 */
// expenses.submit (MKK module 23, 2026-10-24) reaches an old-style volunteer the same way, and the role carries it too.
const VOLUNTEER_LEGACY_EXTRA = ['planner.read', 'operations.read', 'expenses.submit'];

/**
 * Is this an old-style volunteer (an editor with revokes) that should be a `volunteer`? Then the change, for an operator
 * or super admin to apply on purpose — never automatically: { role: 'volunteer', grants: [], revokes: [], loses }.
 * `loses`: what the account can do now and will not as a volunteer (shown before it is applied). null otherwise.
 */
function volunteerMigration(user) {
  if (!user || user.role !== 'editor') return null;
  const member = new Set(permissionsForRole('member'));
  const staffTier = permissionsFor(user).filter(p => !member.has(p));
  if (!staffTier.includes('work.assigned')) return null;
  if (staffTier.some(p => !VOLUNTEER_KEEPS.includes(p) && !VOLUNTEER_LEGACY_EXTRA.includes(p))) return null;
  return { role: 'volunteer', grants: [], revokes: [], loses: staffTier.filter(p => !permissionsForRole('volunteer').includes(p)).sort() };
}

/** The full permission set a role carries, following `inherits`. */
function permissionsForRole(role) {
  const out = new Set();
  let cur = role;
  const guard = new Set();
  while (cur && ROLE_DEFS[cur] && !guard.has(cur)) {
    guard.add(cur);
    for (const p of ROLE_DEFS[cur].permissions) out.add(p);
    cur = ROLE_DEFS[cur].inherits;
  }
  return [...out].sort();
}

/**
 * What a specific USER may do: their role's bundle, plus anything explicitly
 * granted to them, minus anything explicitly revoked.
 *
 * Revokes are applied last and win. A super admin taking one capability away
 * from an otherwise-trusted person should not have to invent a new role for
 * them, and it must not be silently undone by the role bundle.
 */
function permissionsFor(user) {
  // No user at all is a visitor — that is the whole public tier, and the
  // governing rule is that it works without signing in.
  if (!user) return permissionsForRole('visitor');
  // A user record with a role we do not recognise gets NOTHING. It is not a
  // visitor: a stored role we cannot interpret is a data problem, and quietly
  // downgrading it to the public tier hides that a real account has become
  // unreadable. Denial is the safe reading of an unknown grant.
  const role = user.role;
  if (!ROLE_DEFS[role]) return [];
  const set = new Set(permissionsForRole(role));

  const staff = ROLE_DEFS[role].staff;
  // A volunteer's bundle is the whole of it: no grant adds to it (a stored one, from before, is ignored too).
  for (const p of ROLE_DEFS[role].volunteer ? [] : user.grants || []) {
    // A grant of something that does not exist is ignored, not stored as a
    // mystery capability. And a public tier cannot be handed staff powers by
    // a stray grant — those accounts do not belong in the admin panel at all.
    if (!PERMISSIONS.includes(p)) continue;
    if (!staff && !permissionsForRole('member').includes(p)) continue;
    // A partner capability cannot be handed to somebody by a grant row —
    // that is how a staff account would quietly become a membership oracle.
    if (PARTNER_ONLY.includes(p)) continue;
    set.add(p);
  }
  for (const p of user.revokes || []) set.delete(p);
  return [...set].sort();
}

/**
 * The check. Accepts either a role string (the public tiers, where there is
 * no user record) or a user object with `role` and optional grants/revokes.
 */
function can(who, permission) {
  if (!PERMISSIONS.includes(permission)) return false;   // unknown = denied
  const user = typeof who === 'string' ? { role: who } : who;
  return permissionsFor(user).includes(permission);
}

const isStaff = (who) => {
  const role = typeof who === 'string' ? who : (who && who.role);
  return Boolean(ROLE_DEFS[role] && ROLE_DEFS[role].staff);
};

/**
 * Approving is deliberately NOT just a permission.
 *
 * Nobody approves their own message, whatever their role and whatever a super
 * admin granted them. This is above the permission system on purpose: the
 * whole point of a second pair of eyes is that it belongs to a second pair of
 * eyes, and a system where the right grant makes that rule disappear does not
 * have the rule at all.
 */
function canApprove(actor, item, permission = 'push.approve') {
  if (!actor) return { ok: false, reason: 'not_permitted' };
  if (!can(actor, permission)) return { ok: false, reason: 'not_permitted' };
  if (item && item.created_by === actor.id) return { ok: false, reason: 'cannot_approve_own' };
  return { ok: true };
}

/** Kept for the existing call sites; `canApprove` is the general form. */
const canApprovePush = (actor, message) => canApprove(actor, message, 'push.approve');

/**
 * The "link anyway" override on a member link is an admin ROLE's decision, not a permission (owner, 2026-10-10,
 * ML-D01 P2-B). It gives a second account a membership somebody already holds, so it belongs to the admin tier:
 * the role must be admin or above (followed through `inherits`), and the person must still hold `members.link`.
 * A grant cannot hand it to an editor, and revoking `members.link` from an admin takes it away.
 */
function canOverrideMemberLink(actor) {
  if (!actor || !can(actor, 'members.link')) return false;
  for (let r = actor.role, seen = new Set(); r && ROLE_DEFS[r] && !seen.has(r); seen.add(r), r = ROLE_DEFS[r].inherits) {
    if (r === 'admin') return true;
  }
  return false;
}

/**
 * Changing what somebody may do. Only a super admin, and three refusals that
 * exist so the permission system cannot be used to dismantle itself.
 */
/** May `actor` change `target`'s individual permissions at all (rule 6)? The panel greys the column out otherwise. */
function mayChangePermissions(actor, target) {
  if (!actor || !target || target.id === actor.id || !can(actor, 'permissions.grant') || !isStaff(target)) return false;
  if (ROLE_DEFS[target.role] && ROLE_DEFS[target.role].volunteer) return false;
  const mine = new Set(permissionsFor(actor));
  const theirs = permissionsFor(target);
  return theirs.every((p) => mine.has(p)) && theirs.length < mine.size;
}

function changePermissions(actor, target, change = {}, allStaff = []) {
  if (!can(actor, 'permissions.grant')) return { ok: false, reason: 'not_permitted' };
  if (!target) return { ok: false, reason: 'unknown_user' };

  const unknown = [...(change.grant || []), ...(change.revoke || [])]
    .filter(p => !PERMISSIONS.includes(p));
  if (unknown.length) return { ok: false, reason: 'unknown_permission', unknown };
  const partnerOnly = (change.grant || []).filter(p => PARTNER_ONLY.includes(p));
  if (partnerOnly.length) return { ok: false, reason: 'partner_only', unknown: partnerOnly };

  if (!isStaff(target)) return { ok: false, reason: 'not_a_staff_account' };
  // A volunteer is exactly the role: nothing granted, and nothing revoked either (a revoke would only be left behind,
  // to take effect if the person were later given another role).
  if (ROLE_DEFS[target.role].volunteer && ((change.grant || []).length || (change.revoke || []).length)) {
    return { ok: false, reason: 'volunteer_fixed',
             message_no: 'En frivillig har nøyaktig rollens tilgang, verken mer eller mindre. Gi personen en annen rolle i stedet.' };
  }

  // FAULT 124 (batch 18, found building the live Tilganger matrix). Nobody
  // changes their OWN access — the same rule staff-admin applies to a role
  // and to deactivation, and the one the panel has always stated in words
  // ("Du kan ikke endre din egen tilgang"). This function allowed it, the
  // database refuses it (CHECK not_self_granted: granted_by <> staff_user_id),
  // and the route between them answered a super admin's click on their own
  // column with a 500. Refused here, in words, before anything is written.
  if (target.id && target.id === actor.id) {
    return { ok: false, reason: 'own_account',
             message_no: 'Du kan ikke endre din egen tilgang. Be en annen med mer tilgang enn deg (for en superadministrator: Min Masjid).' };
  }

  // Rule 6 for permissions (owner, 2026-10-14), as for roles (staff-admin.changeRole): only somebody strictly below the
  // actor, and never a permission the actor does not hold. Otherwise a super admin could revoke one permission from
  // another and then demote them, and an administrator holding permissions.grant could hand out what they lack.
  const mine = new Set(permissionsFor(actor));
  const theirs = permissionsFor(target);
  if (!(theirs.every((p) => mine.has(p)) && theirs.length < mine.size)) {
    return { ok: false, reason: 'not_permitted',
             message_no: 'Du kan bare endre tilgangen til noen med mindre tilgang enn deg.' };
  }
  const beyond = (change.grant || []).filter((p) => !mine.has(p));
  if (beyond.length) {
    return { ok: false, reason: 'not_permitted', unknown: beyond,
             message_no: 'Du kan ikke gi en tilgang du ikke har selv.' };
  }

  // "The last super admin removed their own access" — the lock-out this
  // function used to check for here — cannot happen any more: only a holder
  // of permissions.grant gets this far, and nobody may change their own
  // access, so whoever is changing someone else's keeps theirs. The branch
  // that checked it could no longer fire, and a check that cannot fire is
  // not a safeguard (fault 101), so it is gone. `allStaff` stays in the
  // signature for callers.

  const grants = new Set(target.grants || []);
  const revokes = new Set(target.revokes || []);
  for (const p of change.grant || []) { grants.add(p); revokes.delete(p); }
  for (const p of change.revoke || []) { revokes.add(p); grants.delete(p); }

  const next = { ...target, grants: [...grants].sort(), revokes: [...revokes].sort() };
  return {
    ok: true,
    user: next,
    effective: permissionsFor(next),
    // Every permission change is an audit event. Who may do what is exactly
    // the kind of thing that has to be reconstructable a year later.
    audit: { action: 'permissions.change', actor_id: actor.id, target_id: target.id,
             granted: change.grant || [], revoked: change.revoke || [] },
  };
}

/**
 * What the "who can do what" admin screen renders: every permission, whether
 * this user has it, and — the part that matters — WHY.
 *
 * A checkbox that is ticked because of the person's role looks identical to
 * one ticked by hand, and an administrator who cannot tell them apart will
 * eventually revoke something they meant to leave alone.
 */
function matrixFor(user) {
  const fromRole = new Set(ROLE_DEFS[user.role] ? permissionsForRole(user.role) : []);
  const granted = new Set(user.grants || []);
  const revoked = new Set(user.revokes || []);
  const effective = new Set(permissionsFor(user));
  return PERMISSIONS.map(p => ({
    permission: p,
    allowed: effective.has(p),
    source: revoked.has(p) ? 'revoked'
          : granted.has(p) && !fromRole.has(p) ? 'granted'
          : fromRole.has(p) ? 'role' : 'none',
  }));
}

module.exports = {
  PERMISSIONS, ROLES, STAFF_ROLES, ROLE_DEFS, PARTNER_ONLY, VOLUNTEER_KEEPS, VOLUNTEER_LEGACY_EXTRA, volunteerRevokes, volunteerMigration,
  permissionsForRole, permissionsFor, can, isStaff,
  canApprove, canApprovePush, canOverrideMemberLink, changePermissions, mayChangePermissions, matrixFor,
};
